import * as React from "react"
import { isNetworkRequestInFlight } from "@apollo/client/core/networkStatus"
import {
  Interval,
  ObjectKind,
  type SearchEntitiesQueryResult,
} from "@digits-graphql/frontend/graphql-bearer"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import type QueryBuilder from "src/frontend/components/OS/Applications/Search/QueryBuilder"
import { type QueryRequest } from "src/frontend/components/OS/Applications/Search/QueryBuilder"
import { SearchContext } from "src/frontend/components/OS/Applications/Search/SearchContext"
import useSearchQuery from "src/frontend/components/OS/Applications/Search/SearchQueries/useSearchQuery"
import SearchResults from "src/frontend/components/OS/Applications/Search/SearchResults"
import { NoSearchResultsFound } from "src/frontend/components/OS/Applications/Search/SearchResults/NoResultsFound"
import { SearchPageContainer } from "src/frontend/components/OS/Applications/Search/shared"
import { TimeContext } from "src/shared/components/Contexts/TimeContext"

/*
 STYLES
*/

const Queries = styled(SearchPageContainer)`
  width: 100%;
  background: ${colors.cloudWhite};
  border: 1px solid ${colors.white};
  border-radius: 16px;
  padding: 16px;

  &.hidden {
    display: none;
    pointer-events: none;
  }
`

/*
  INTERFACES
*/

interface SearchQueriesProps {
  builder: QueryBuilder
  setIsSearchRequestInFlight: (isSearching: boolean) => void
}

interface SearchQueryResult {
  query: QueryRequest
  result: SearchEntitiesQueryResult
}

interface QueryProps {
  builder: QueryBuilder
  queryResult: SearchQueryResult
}

/*
  COMPONENTS
*/

const SearchQueries: React.FC<SearchQueriesProps> = ({ builder, setIsSearchRequestInFlight }) => {
  const enable = builder.build().isValid
  const { loading, total, ...queries } = useSearchQueries(builder)
  // Search should always be in the current month so it is not affected by origins from another apps
  const searchOrigin = useConstant(() => dateTimeHelper.defaultIntervalOrigin(Interval.Month))

  React.useEffect(() => {
    setIsSearchRequestInFlight(loading)
  }, [loading, setIsSearchRequestInFlight])

  if (enable && !loading && total === 0) {
    return <NoSearchResultsFound />
  }

  return (
    <TimeContext.Provider value={searchOrigin}>
      <Queries className={stringHelper.classNames(!enable && "hidden")}>
        <SearchQuery builder={builder} queryResult={queries.categories} />
        <SearchQuery builder={builder} queryResult={queries.parties} />
        <SearchQuery builder={builder} queryResult={queries.departments} />
        <SearchQuery builder={builder} queryResult={queries.locations} />
        <SearchQuery builder={builder} queryResult={queries.transactions} />
      </Queries>
    </TimeContext.Provider>
  )
}
export default SearchQueries

const SearchQuery: React.FC<QueryProps> = ({ builder, queryResult: { query, result } }) => (
  <SearchContext.Provider value={React.useMemo(() => ({ builder, query }), [builder, query])}>
    <SearchResults result={result} />
  </SearchContext.Provider>
)

function useSearchQueries(builder: QueryBuilder) {
  const partyQuery = builder.setKind(ObjectKind.Party).build()
  const { result: partyQueryResult, networkStatus: partyNetworkStatus } = useSearchQuery(partyQuery)

  const categoryQuery = builder.setKind(ObjectKind.Category).build()
  const { result: categoryQueryResult, networkStatus: categoryNetworkStatus } =
    useSearchQuery(categoryQuery)

  const departmentQuery = builder.setKind(ObjectKind.Department).build()
  const { result: departmentQueryResult, networkStatus: departmentNetworkStatus } =
    useSearchQuery(departmentQuery)

  const locationQuery = builder.setKind(ObjectKind.Location).build()
  const { result: locationQueryResult, networkStatus: locationNetworkStatus } =
    useSearchQuery(locationQuery)

  const transactionQuery = builder.setKind(ObjectKind.Transaction).build()
  const { result: transactionQueryResult, networkStatus: transactionNetworkStatus } =
    useSearchQuery(transactionQuery)

  const loading =
    isNetworkRequestInFlight(partyNetworkStatus) ||
    isNetworkRequestInFlight(categoryNetworkStatus) ||
    isNetworkRequestInFlight(departmentNetworkStatus) ||
    isNetworkRequestInFlight(locationNetworkStatus) ||
    isNetworkRequestInFlight(transactionNetworkStatus)

  const total = [
    partyQueryResult,
    categoryQueryResult,
    departmentQueryResult,
    locationQueryResult,
    transactionQueryResult,
  ].reduce((sum, result) => sum + (result.data?.search?.total ?? 0), 0)

  return {
    loading,
    total,

    parties: {
      query: partyQuery,
      result: partyQueryResult,
    },
    categories: {
      query: categoryQuery,
      result: categoryQueryResult,
    },
    departments: {
      query: departmentQuery,
      result: departmentQueryResult,
    },
    locations: {
      query: locationQuery,
      result: locationQueryResult,
    },
    transactions: {
      query: transactionQuery,
      result: transactionQueryResult,
    },
  }
}
